import React, { useCallback } from "react"
import {
  StateWrapper,
  StatusCancel,
  StatusColumn,
  StatusCustom,
  StatusFull,
  StatusPart,
  StatusWrapper
} from "./OrderTable.styled"
import { Tooltip } from "../../Ui/Tooltip"
import { ReactComponent as DeliveryIcon } from "images/icons/truck-ramp-box.svg"
import { ReactComponent as RefundIcon } from "images/icons/arrows-repeat.svg"
import { ReactComponent as CompensationIcon } from "images/icons/hand-holding-dollar.svg"
import { ReactComponent as ReleaseIcon } from "images/icons/cart-arrow-up.svg"
import { ReactComponent as PlacedIcon } from "images/icons/cart-shopping.svg"
import { ReactComponent as CustomIcon } from "images/icons/link-horizontal.svg"
import { ReactComponent as CancelIcon } from "images/icons/ban.svg"

import { getFormattedState } from "../helpers"
import { CustomState, OrderSearchHit } from "lib/types/generated/graphql-types"

type Props = {
  order: OrderSearchHit
  customStates: CustomState[]
}

const TableStatus = ({ order, customStates }: Props) => {
  const orderCustomStates = order?.status?.customStates ?? []

  const getStatus = (state: string, withName?: boolean) => {
    switch (state) {
      case "FULLY_DELIVERED":
        return (
          <StatusFull $withName={withName}>
            <DeliveryIcon />
            {withName && "Delivered"}
          </StatusFull>
        )
      case "PARTIALLY_DELIVERED":
        return (
          <StatusPart $withName={withName}>
            <DeliveryIcon />
            {withName && "Partially"}
          </StatusPart>
        )
      case "PARTIALLY_REFUNDED":
        return (
          <StatusPart>
            <RefundIcon />
          </StatusPart>
        )
      case "FULLY_REFUNDED":
        return (
          <StatusFull>
            <RefundIcon />
          </StatusFull>
        )
      case "FULLY_COMPENSATED":
        return (
          <StatusFull>
            <CompensationIcon />
          </StatusFull>
        )
      case "PARTIALLY_COMPENSATED":
        return (
          <StatusPart>
            <CompensationIcon />
          </StatusPart>
        )
      case "CANCELLED":
        return (
          <StatusCancel $withName={withName}>
            <CancelIcon />
            Cancelled
          </StatusCancel>
        )
      case "PLACED":
        return (
          <StatusFull $withName={withName}>
            <PlacedIcon />
            {withName && "Placed"}
          </StatusFull>
        )
      case "RELEASED":
        return (
          <StatusFull>
            <ReleaseIcon />
          </StatusFull>
        )
      default:
        return state
    }
  }

  const getCustomState = useCallback(
    (id: string) => customStates?.find((s) => s.id === id),
    [customStates]
  )

  const getCustomStates = () => {
    return (
      <>
        <Tooltip id={`custom-${order.id}`}>
          <div>
            {order?.status?.customStates?.map((state) => (
              <div key={state.id}>{getCustomState(state.id)?.description}</div>
            ))}
          </div>
        </Tooltip>
        <StatusCustom data-tooltip-id={`custom-${order.id}`}>
          <CustomIcon />
        </StatusCustom>
      </>
    )
  }

  const getOrderStatus = () => {
    if (order.status.orderStates.length === 1) {
      return getStatus(order.status.orderStates[0], true)
    }

    return (
      <>
        <Tooltip id={`status-${order.id}`}>
          <div>
            {order?.status?.orderStates.map((state) => <div key={state}>{getFormattedState(state)}</div>)}
          </div>
        </Tooltip>
        {order.status.orderStates.map((status) => <React.Fragment key={status}>{getStatus(status)}</React.Fragment>)}
      </>
    )
  }

  return (
    <StatusColumn className="hideOnSmallScreens hideOnMediumScreens">
      <StatusWrapper>
        <StateWrapper data-tooltip-id={`status-${order.id}`}>{getOrderStatus()}</StateWrapper>
        {orderCustomStates.length > 0 && getCustomStates()}
      </StatusWrapper>
    </StatusColumn>
  )
}

export default TableStatus
